import { z } from 'zod';

import {
  insightCitationSchema,
  otherThemeSchema,
  ratingSchema,
  themeSchema,
} from './earnings-insights';

const catalystInsightTypeSchema = z.enum(['company', 'theme']);
export type CatalystInsightType = z.infer<typeof catalystInsightTypeSchema>;

const identifierSchema = z.string().describe('entity_tag or theme_id');

const catalystSubcategoriesSchema = z.enum([
  'financial_operational_updates',
  'corporate_actions_strategic_announcements',
  'management_leadership_changes',
  'legal_regulatory_events',
]);

export type CatalystSubcategories = z.infer<typeof catalystSubcategoriesSchema>;

export const catalystInsightSchema = z.object({
  id: z.string(),
  category: catalystInsightTypeSchema,
  identifier: identifierSchema,
  title: z.string(),
  sub_category: catalystSubcategoriesSchema,
  action_type: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const sectionGroupSchema = z.object({
  title: z.string(),
  sections: z.array(
    z.object({
      title: z.string(),
      content: z.array(z.string()),
    })
  ),
});

const impactedCompanySchema = z.object({
  entity_tag: z.string(),
  rating: ratingSchema,
  description: z.string(),
  relationship: z.string(),
});

const detailedCatalystInsightSchema = catalystInsightSchema.extend({
  snakes: z.array(z.string()).optional(),
  section_groups: z.array(sectionGroupSchema),
  themes: z.array(themeSchema),
  other_themes: z.array(otherThemeSchema),
  impacted_companies: z.array(impactedCompanySchema),
  citations: z.array(insightCitationSchema),
});

const catalystInsightFilterResponseSchema = z.array(catalystInsightSchema);

export const catalystInsightFilterReqSchema = z.object({
  entities: z.array(z.string()).optional(),
  themes: z.array(z.string()).optional(),
  categories: z.array(catalystInsightTypeSchema).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  page_size: z.number().optional(),
});

export type CatalystInsight = z.infer<typeof catalystInsightSchema>;
export type CatalystInsightFilterResponse = z.infer<
  typeof catalystInsightFilterResponseSchema
>;
export type CatalystInsightFilterRequest = z.infer<
  typeof catalystInsightFilterReqSchema
>;
export type DetailedCatalystInsight = z.infer<
  typeof detailedCatalystInsightSchema
>;

export const catalystInsights = {
  postCatalystInsightFilter: {
    path: `attribution-insights/v1/filter`,
    schema: catalystInsightFilterResponseSchema,
  },
  getCatalystInsight: {
    path: (insightId: string) => `attribution-insights/v1/${insightId}`,
    schema: detailedCatalystInsightSchema,
  },
};
